import { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { WHEN_LABELS, type V2ReportNeed, type V2TimingGroup } from '../api/v2ReportApi';
import { V2NeedDetail } from './V2NeedDetail';

const chevron = (open: boolean) =>
  `h-4 w-4 shrink-0 text-gray-500 transition-transform motion-reduce:transition-none ${open ? 'rotate-180' : ''}`;

/** One need in a group: its title only, opening to the full detail. */
function NeedRow({ need }: { need: V2ReportNeed }) {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  return (
    <li className="border-t border-gray-100">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center gap-3 px-5 py-3.5 text-left hover:bg-[#fbf9f5]"
      >
        <ChevronDown className={chevron(open)} aria-hidden />
        <span className="text-[15px] font-medium text-gray-900">{need.title}</span>
      </button>
      {open && (
        <div id={panelId} className="px-5 pb-5 sm:pl-12">
          <V2NeedDetail need={need} />
        </div>
      )}
    </li>
  );
}

/** A timing group: a fixed heading over its needs, so only a need ever folds. */
function Group({ group }: { group: V2TimingGroup }) {
  const headingId = useId();
  const n = group.needs.length;
  return (
    <section aria-labelledby={headingId}>
      <div className="mb-2 flex items-baseline justify-between gap-4 px-1">
        <h3 id={headingId} className="font-serif text-[17px] font-semibold text-gray-900">
          {WHEN_LABELS[group.when]}
          <span className="ml-2 font-sans text-sm font-normal text-gray-500">{group.guidance}</span>
        </h3>
        <span className="shrink-0 rounded-full bg-[#f3efe6] px-2.5 py-1 text-xs font-medium text-gray-700">
          {n} {n === 1 ? 'need' : 'needs'}
        </span>
      </div>
      <ul
        aria-labelledby={headingId}
        className="overflow-hidden rounded-2xl border border-gray-200 bg-white [&>li:first-child]:border-t-0"
      >
        {group.needs.map((need) => (
          <NeedRow key={need.needId} need={need} />
        ))}
      </ul>
    </section>
  );
}

/**
 * "Everything else, by timing": the groups the payload sends, soonest first,
 * as fixed headings; each need is folded to its title until opened, the one
 * level that folds. No status here — only the top 3 take one.
 */
export function V2TimingGroups({ groups }: { groups: V2TimingGroup[] }) {
  return (
    <div className="space-y-3">
      {groups.map((group) => (
        <Group key={group.when} group={group} />
      ))}
    </div>
  );
}
